/** A plain SVG line chart: one series, touch and mouse read-out, gaps where the device was silent. */
import { useEffect, useMemo, useRef, useState } from "react";
import { formatNumber, niceTicks, splitAtGaps } from "./format.ts";

export interface Point {
  at: number;
  value: number;
}

const PAD = { top: 26, right: 12, bottom: 26, left: 46 };

/** gaps: false for on/off changes only, where a long time between points means "no change", not silence. */
export function LineChart({ points, unit, onOff, from, to, gaps = true }: { points: Point[]; unit: string; onOff: boolean; from: number; to: number; gaps?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<Point | null>(null);
  const height = 240;

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.max(260, e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sorted = useMemo(() => [...points].sort((a, b) => a.at - b.at), [points]);
  const { yTicks, yMin, yMax } = useMemo(() => {
    if (onOff) return { yTicks: [0, 1], yMin: -0.15, yMax: 1.15 };
    const vals = sorted.map((p) => p.value);
    const t = niceTicks(Math.min(...vals), Math.max(...vals));
    return { yTicks: t, yMin: t[0]!, yMax: t.at(-1)! };
  }, [sorted, onOff]);

  const w = width - PAD.left - PAD.right;
  const h = height - PAD.top - PAD.bottom;
  const x = (t: number) => PAD.left + ((t - from) / (to - from)) * w;
  const y = (v: number) => PAD.top + (1 - (v - yMin) / (yMax - yMin || 1)) * h;

  const runs = gaps ? splitAtGaps(sorted) : [sorted];
  const pathFor = (run: Point[]) =>
    onOff
      ? // Relays and switches hold their state until the next report: draw steps.
        run.map((p, i) => (i === 0 ? `M${x(p.at)},${y(p.value)}` : `H${x(p.at)}V${y(p.value)}`)).join("")
      : run.map((p, i) => `${i ? "L" : "M"}${x(p.at).toFixed(1)},${y(p.value).toFixed(1)}`).join("");

  const xTicks = useMemo(() => {
    const span = to - from;
    const day = 86_400_000;
    const step = span <= day * 1.1 ? 6 * 3_600_000 : span <= 8 * day ? day : 7 * day;
    const ticks: number[] = [];
    // Align to local midnight / 6-hour marks.
    const start = new Date(from);
    start.setMinutes(0, 0, 0);
    if (step >= day) start.setHours(0);
    else start.setHours(Math.ceil(start.getHours() / 6) * 6);
    for (let t = start.getTime(); t <= to; t += step) if (t >= from) ticks.push(t);
    // On a phone, drop every other label rather than let them run together.
    const room = (width - PAD.left - PAD.right) / Math.max(1, ticks.length);
    const every = room < 64 ? Math.ceil(64 / room) : 1;
    return { ticks: ticks.filter((_, i) => i % every === 0), daily: step >= day };
  }, [from, to, width]);

  const pick = (clientX: number) => {
    const el = box.current;
    if (!el || !sorted.length) return;
    const rect = el.getBoundingClientRect();
    const t = from + ((clientX - rect.left - PAD.left) / w) * (to - from);
    let best = sorted[0]!;
    for (const p of sorted) if (Math.abs(p.at - t) < Math.abs(best.at - t)) best = p;
    setHover(best);
  };

  const label = (v: number) => (onOff ? (v ? "On" : "Off") : `${formatNumber(v)}${unit ? ` ${unit}` : ""}`);

  return (
    <div
      className="chart-box"
      ref={box}
      onPointerMove={(e) => pick(e.clientX)}
      onPointerDown={(e) => pick(e.clientX)}
      onPointerLeave={() => setHover(null)}
    >
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Chart, ${sorted.length} readings`}>
        {yTicks.map((v) => (
          <g key={v}>
            <line className="grid" x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} />
            <text className="axis" x={PAD.left - 6} y={y(v) + 4} textAnchor="end">
              {onOff ? (v ? "On" : "Off") : formatNumber(v)}
            </text>
          </g>
        ))}
        {xTicks.ticks.map((t) => (
          <text key={t} className="axis" x={x(t)} y={height - 6} textAnchor="middle">
            {xTicks.daily
              ? new Date(t).toLocaleDateString("en-AU", { weekday: "short", day: "numeric" })
              : new Date(t).toLocaleTimeString("en-AU", { hour: "numeric" })}
          </text>
        ))}
        {unit && !onOff && (
          <text className="axis" x={PAD.left - 6} y={14} textAnchor="end">
            {unit}
          </text>
        )}
        {runs.map((run, i) => (
          <g key={i}>
            {!onOff && run.length > 1 && (
              <path className="area" d={`${pathFor(run)}L${x(run.at(-1)!.at)},${PAD.top + h}L${x(run[0]!.at)},${PAD.top + h}Z`} />
            )}
            <path className="line" d={pathFor(run)} />
            {run.length === 1 && <circle className="pt" cx={x(run[0]!.at)} cy={y(run[0]!.value)} r={3} />}
          </g>
        ))}
        {hover && (
          <g>
            <line className="cross" x1={x(hover.at)} x2={x(hover.at)} y1={PAD.top} y2={PAD.top + h} />
            <circle className="pt" cx={x(hover.at)} cy={y(hover.value)} r={4.5} />
          </g>
        )}
      </svg>
      {hover && (
        <div className="tip" style={{ left: Math.min(Math.max(x(hover.at), 70), width - 70) }}>
          {label(hover.value)} ·{" "}
          {new Date(hover.at).toLocaleString("en-AU", { weekday: "short", hour: "numeric", minute: "2-digit" })}
        </div>
      )}
    </div>
  );
}

/**
 * Daily totals as bars: one series, values from zero, a read-out on hover or tap. Bars have
 * rounded tops and a small gap so neighbours stay apart when there are many days.
 */
export function BarChart({ days, values, unit, color = "var(--water)", label }: { days: string[]; values: number[]; unit: string; color?: string; label: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);
  const height = 220;
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.max(260, e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const ticks = niceTicks(0, Math.max(...values, 0) || 1);
  const top = ticks.at(-1)!;
  const w = width - PAD.left - PAD.right;
  const h = height - PAD.top - PAD.bottom;
  const slot = w / Math.max(1, days.length);
  const gap = Math.max(2, Math.min(8, slot * 0.25));
  const bw = Math.max(1, slot - gap);
  const y = (v: number) => PAD.top + (1 - v / top) * h;
  const day = (d: string, opts: Intl.DateTimeFormatOptions) => new Date(`${d}T12:00:00`).toLocaleDateString("en-AU", opts);
  // Label every day when there's room, otherwise every few.
  const every = Math.ceil(56 / slot);
  const bar = (x: number, v: number) => {
    const yy = y(v);
    const r = Math.min(4, bw / 2, PAD.top + h - yy);
    return `M${x},${PAD.top + h}V${yy + r}Q${x},${yy} ${x + r},${yy}H${x + bw - r}Q${x + bw},${yy} ${x + bw},${yy + r}V${PAD.top + h}Z`;
  };
  return (
    <div
      className="chart-box"
      ref={box}
      style={{ height }}
      onPointerLeave={() => setHover(null)}
      onPointerMove={(e) => {
        const rect = box.current!.getBoundingClientRect();
        const i = Math.floor((e.clientX - rect.left - PAD.left) / slot);
        setHover(i >= 0 && i < days.length ? i : null);
      }}
    >
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label} per day`}>
        {ticks.map((v) => (
          <g key={v}>
            <line className="grid" x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} />
            <text className="axis" x={PAD.left - 6} y={y(v) + 4} textAnchor="end">
              {formatNumber(v)}
            </text>
          </g>
        ))}
        <text className="axis" x={PAD.left - 6} y={14} textAnchor="end">
          {unit}
        </text>
        {values.map((v, i) =>
          v > 0 ? <path key={i} d={bar(PAD.left + i * slot + gap / 2, v)} fill={color} opacity={hover === null || hover === i ? 1 : 0.45} /> : null,
        )}
        {days.map((d, i) =>
          (days.length - 1 - i) % every === 0 ? (
            <text key={d} className="axis" x={PAD.left + i * slot + slot / 2} y={height - 6} textAnchor="middle">
              {days.length <= 10 ? day(d, { weekday: "short", day: "numeric" }) : day(d, { day: "numeric", month: "short" })}
            </text>
          ) : null,
        )}
      </svg>
      {hover !== null && (
        <div className="tip" style={{ left: Math.min(Math.max(PAD.left + hover * slot + slot / 2, 80), width - 80) }}>
          {day(days[hover]!, { weekday: "short", day: "numeric", month: "short" })} · {formatNumber(values[hover]!)} {unit}
        </div>
      )}
    </div>
  );
}
